/**
 * JSON Import Modal
 * Enhanced modal for importing diagram JSON with:
 * - Automatic cleaning of escaped JSON
 * - Collapsible tree view
 * - Raw text editor
 * - Validation and error display
 */

import { useState, useEffect, memo } from 'react'
import Modal from './Modal'
import { autoCleanAndValidate, type CleanJSONResult } from '../../utils/jsonCleaner'
import { JsonEditor } from 'json-edit-react'
import './JSONImportModal.css'

export interface JSONImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (data: any) => void
}

// Simple collapsible JSON tree component
interface JSONTreeProps {
  data: any
  name?: string
  level?: number
}

function JSONTree({ data, name, level = 0 }: JSONTreeProps) {
  const [isCollapsed, setIsCollapsed] = useState(level > 2) // Auto-collapse deep levels

  const indent = level * 20

  if (data === null) {
    return (
      <div style={{ marginLeft: indent }} className="json-tree-item">
        {name && <span className="json-key">{name}: </span>}
        <span className="json-null">null</span>
      </div>
    )
  }

  if (typeof data !== 'object') {
    const type = typeof data
    return (
      <div style={{ marginLeft: indent }} className="json-tree-item">
        {name && <span className="json-key">{name}: </span>}
        <span className={`json-${type}`}>
          {type === 'string' ? `"${data}"` : String(data)}
        </span>
      </div>
    )
  }

  const isArray = Array.isArray(data)
  const entries = isArray ? data : Object.entries(data)
  const isEmpty = entries.length === 0

  return (
    <div style={{ marginLeft: indent }} className="json-tree-node">
      <div className="json-tree-header" onClick={() => !isEmpty && setIsCollapsed(!isCollapsed)}>
        {!isEmpty && (
          <span className="json-tree-toggle">{isCollapsed ? '▶' : '▼'}</span>
        )}
        {name && <span className="json-key">{name}: </span>}
        <span className="json-bracket">
          {isArray ? '[' : '{'}
          {isEmpty && (isArray ? ']' : '}')}
          {!isEmpty && isCollapsed && (
            <span className="json-collapsed-indicator">
              ... {entries.length} {isArray ? 'items' : 'properties'}
            </span>
          )}
        </span>
      </div>
      {!isCollapsed && !isEmpty && (
        <div className="json-tree-children">
          {isArray
            ? data.map((item: any, index: number) => (
                <JSONTree key={index} data={item} name={`[${index}]`} level={level + 1} />
              ))
            : entries.map(([key, value]: [string, any]) => (
                <JSONTree key={key} data={value} name={key} level={level + 1} />
              ))}
        </div>
      )}
      {!isCollapsed && !isEmpty && (
        <div style={{ marginLeft: indent }} className="json-bracket">
          {isArray ? ']' : '}'}
        </div>
      )}
    </div>
  )
}

function JSONImportModal({ isOpen, onClose, onImport }: JSONImportModalProps) {
  console.log('🔷 [JSONImportModal] Component rendered, isOpen:', isOpen)

  const [rawJSON, setRawJSON] = useState('')
  const [viewMode, setViewMode] = useState<'raw' | 'tree' | 'editor'>('raw')
  const [cleanResult, setCleanResult] = useState<CleanJSONResult | null>(null)
  const [showAutoCleaned, setShowAutoCleaned] = useState(false)
  const [editableData, setEditableData] = useState<any>(null)

  // Auto-clean and validate when raw JSON changes
  useEffect(() => {
    if (rawJSON.trim()) {
      console.log('🔍 [JSONImportModal] Processing JSON input...')
      const result = autoCleanAndValidate(rawJSON)
      console.log('🔍 [JSONImportModal] Validation result:', result)
      setCleanResult(result)

      // Auto-switch to editor view if valid
      if (result.success) {
        console.log('✅ [JSONImportModal] JSON valid, switching to editor view')
        setEditableData(result.parsed)
        setViewMode('editor')
        setShowAutoCleaned(!!result.wasEscaped)
      } else {
        console.error('❌ [JSONImportModal] JSON validation failed:', result.error)
        setEditableData(null)
      }
    } else {
      setCleanResult(null)
      setShowAutoCleaned(false)
      setEditableData(null)
    }
  }, [rawJSON])

  const handleImport = () => {
    console.log('🔵 [JSONImportModal] Import button clicked')
    console.log('🔵 [JSONImportModal] Current view mode:', viewMode)
    console.log('🔵 [JSONImportModal] Clean result:', cleanResult)
    console.log('🔵 [JSONImportModal] Editable data:', editableData)

    // Use editableData if in editor mode (may have been edited), otherwise use cleanResult
    const dataToImport = viewMode === 'editor' && editableData ? editableData : cleanResult?.parsed

    if (dataToImport) {
      console.log('✅ [JSONImportModal] Validation passed, calling onImport')
      console.log('📦 [JSONImportModal] Data to import:', dataToImport)
      console.log('📊 [JSONImportModal] Nodes count:', dataToImport.nodes?.length)
      console.log('🔗 [JSONImportModal] Edges count:', dataToImport.edges?.length)

      onImport(dataToImport)
      handleClose()

      console.log('✅ [JSONImportModal] Import completed, modal closed')
    } else {
      console.error('❌ [JSONImportModal] Import failed - no valid data')
      console.error('❌ [JSONImportModal] Clean result:', cleanResult)
      console.error('❌ [JSONImportModal] Editable data:', editableData)
    }
  }

  const handleClose = () => {
    setRawJSON('')
    setCleanResult(null)
    setShowAutoCleaned(false)
    setEditableData(null)
    setViewMode('raw')
    onClose()
  }

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      setRawJSON(text)
    } catch (error) {
      console.error('Failed to read clipboard:', error)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Diagram from JSON">
      <div className="json-import-modal">
        {/* Header with actions */}
        <div className="json-import-header">
          <div className="json-import-tabs">
            <button
              className={viewMode === 'raw' ? 'active' : ''}
              onClick={() => setViewMode('raw')}
            >
              📝 Raw JSON
            </button>
            <button
              className={viewMode === 'tree' ? 'active' : ''}
              onClick={() => setViewMode('tree')}
              disabled={!cleanResult?.success}
            >
              🌲 Tree View
            </button>
            <button
              className={viewMode === 'editor' ? 'active' : ''}
              onClick={() => setViewMode('editor')}
              disabled={!editableData}
            >
              ✏️ Editor
            </button>
          </div>
          <button className="paste-button" onClick={handlePaste} title="Paste from clipboard">
            📋 Paste
          </button>
        </div>

        {/* Auto-cleaned notice */}
        {showAutoCleaned && cleanResult?.success && (
          <div className="json-auto-cleaned-notice">
            ✨ Escaped JSON detected and automatically cleaned!
          </div>
        )}

        {/* Content area */}
        <div className="json-import-content">
          {viewMode === 'raw' ? (
            <textarea
              className="json-input-textarea"
              value={rawJSON}
              onChange={(e) => setRawJSON(e.target.value)}
              placeholder={`Paste your diagram JSON here...\n\nSupports:\n• Regular JSON\n• Escaped JSON (with \\n, \\", etc.)\n• Auto-cleaning and validation\n• Interactive editor with tree view`}
              spellCheck={false}
            />
          ) : viewMode === 'tree' && cleanResult?.success ? (
            <div className="json-tree-view">
              <JSONTree data={cleanResult.parsed} />
            </div>
          ) : viewMode === 'editor' && editableData ? (
            <div className="json-editor-view">
              <JsonEditor
                data={editableData}
                setData={setEditableData}
                rootName="diagram"
                collapse={2}
                enableClipboard={true}
                restrictEdit={false}
                restrictDelete={false}
                restrictAdd={false}
              />
            </div>
          ) : (
            <div className="json-placeholder">
              {!cleanResult?.success
                ? 'Switch to Raw JSON tab to paste your diagram'
                : 'Paste valid JSON to enable Editor view'}
            </div>
          )}
        </div>

        {/* Error display */}
        {cleanResult && !cleanResult.success && (
          <div className="json-error">
            <strong>❌ Error:</strong> {cleanResult.error}
          </div>
        )}

        {/* Validation info */}
        {cleanResult?.success && (
          <div className="json-validation-info">
            <div className="json-stats">
              <span>✅ Valid diagram JSON</span>
              <span>• {cleanResult.parsed.nodes?.length || 0} nodes</span>
              <span>• {cleanResult.parsed.edges?.length || 0} edges</span>
              {cleanResult.parsed.containers && (
                <span>• {cleanResult.parsed.containers.length} containers</span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <button onClick={handleClose} className="button-secondary">
            Cancel
          </button>
          <button
            onClick={handleImport}
            className="button-primary"
            disabled={!cleanResult?.success}
          >
            Import Diagram
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default memo(JSONImportModal)
